import {
    ActivitiesContinueYourBookingCardSeenEventActivitiesFieldsV2,
    ActivitiesContinueYourBookingContainerSeenEventActivitiesFieldsV1,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesContinueYourBookingCardSeenEventActivitiesManifestV2,
    ActivitiesContinueYourBookingContainerSeenEventActivitiesManifestV1,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { Box, Card, HorizontalScroll, Icon, Image, Link, SkeletonLoader, Tooltip, Typography } from '@drone-js/core';
import { ContinueYourBookingProps, mapDispatchToProps, mapStateToProps } from 'component/page/home/ContinueYourBooking/ContinueYourBooking.selectors';
import { connect, useSelector } from 'react-redux';
import { selectCmsFromBootstrap, selectConfigFromBootstrap } from 'store/slice/bootstrap';

import { MarginType } from '@drone-js/theme/dist/types/Theme';
import { MeasurementStatus } from 'core/constant/MeasurementStatus';
import MockActivityContentMetaInfo from 'mock/continueYourBooking/mock-continueYourBooking';
import React from 'react';
import { getContinueYourBookingActivities } from 'store/api/externalApi';
import { getDetailUrl, getPaxQueryParams } from 'core/utils/activityUrlGenerator';
import { getDateInYmdFormat, getTodayDate } from 'core-framework/util/datetime/date';
import { selectPageTypeId } from 'store/slice/navigation';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ContinueYourBookingActivity, ContinueYourBookingCardProps, ContinueYourBookingPlaceholderProps } from 'component/page/home/ContinueYourBooking/ContinueYourBooking.types';
import { getTravelerInfo, parseContentMetaInfoToContinueYourBookingActivities } from 'component/page/home/ContinueYourBooking/ContinueYourBookingUtils';
import { ActivitiesCampaignCardId, CuscoCampaignResponse } from 'core/type/CuscoResponse.types';
import { measureCuscoCampaignsApiResponse } from 'store/api/measurements/cuscoCampaignsApiMeasurement';

const NUMBER_OF_ACTIVITIES = 5;

const ContinueYourBookingPlaceholder: React.FC<ContinueYourBookingPlaceholderProps> = (props) => {
    const cms = useSelector(selectCmsFromBootstrap);
    return (
        <HorizontalScroll pt="16" pb="24" data-testid="continue-your-booking-placeholder">
            {[...Array<number>(NUMBER_OF_ACTIVITIES)].map((_, i) => (
                <Box key={i} me="12" width={props.size.width} height={props.size.height}>
                    <Card type="interactive" display="flex" width={props.size.width} height={props.size.height} alignItems="center">
                        <Box width={props.size.imageWidth} height={props.size.height} borderRadius={['16', '0', '0', '16']} overflow="hidden">
                            <SkeletonLoader label={cms[326495]} width="100%" height="100%" />
                        </Box>
                        <Box display="flex" flexDirection="row" mx={props.size.marginS} alignItems="center" tabIndex={-1}>
                            <Box display="flex" width="155px" me={props.size.marginS} flexDirection="column" flexGrow={1} justifyContent="center">
                                <SkeletonLoader label={cms[326495]} width="100%" height="11px" me="12" mb="2" />
                                <SkeletonLoader label={cms[326495]} width="100%" height="11px" me="12" mb="8" />
                                <SkeletonLoader label={cms[326495]} width="120px" height="11px" me="12" />
                            </Box>
                            <Icon id="fill.arrow.thin-right" color="base.subtle" size="16" />
                        </Box>
                    </Card>
                </Box>
            ))}
        </HorizontalScroll>
    );
};

const tabletOrDesktop = {
    width: '270px',
    height: '84px',
    imageWidth: '64px',
    marginS: '12' as MarginType,
    marginY: '16' as MarginType,
    marginE: '16' as MarginType,
    targetLink: '_blank',
};

const mobile = {
    width: '255px',
    height: '70px',
    imageWidth: '56px',
    marginS: '8' as MarginType,
    marginY: '12' as MarginType,
    marginE: '12' as MarginType,
    targetLink: '_self',
};

ContinueYourBookingPlaceholder.displayName = 'ContinueYourBookingPlaceholder';

const ContinueYourBookingCard: React.FC<ContinueYourBookingCardProps> = (props) => {
    const { activity, index, size, cms, language, onClick } = props;

    React.useEffect(() => {
        if (activity && Object.keys(activity).length > 0) {
            const paxList = getPaxQueryParams(activity.pax);
            trackElementEvent<ActivitiesContinueYourBookingCardSeenEventActivitiesFieldsV2>(
                ActivitiesContinueYourBookingCardSeenEventActivitiesManifestV2.ids,
                {
                    activity_id: activity.activityId,
                    travel_date: getDateInYmdFormat(activity.travelDate),
                    offer_id: activity.enrichedOfferId,
                    offer_option_token: activity.offerOptionId,
                    action_element_value: paxList,
                },
                { action_element_index: index + 1 },
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Track seen event should only run once
    }, []);

    if (!activity || Object.keys(activity).length === 0 || !activity.activityTitle) {
        return null;
    }

    const todayDate = getTodayDate();
    const getUrl = () => {
        return getDetailUrl(
            activity.activityId,
            language.cultureCode,
            activity.cityId,
            todayDate < activity.travelDate ? activity.travelDate : undefined,
            undefined,
            undefined,
            activity.enrichedOfferId,
            activity.offerOptionId,
            activity.pax,
        );
    };

    return (
        <Link key={index} href={getUrl()} target={size.targetLink} display="flex" onClick={() => onClick(activity, index)}>
            <Card
                data-element-name="activities-continue-your-booking-card"
                data-element-index={index + 1}
                data-testid="continue-your-booking-card"
                type="interactive"
                display="flex"
                width={size.width}
                height={size.height}
                mt="4"
                ms="4"
                mb="24"
                alignItems="center"
                borderRadius="16"
                overflow="hidden"
            >
                <Tooltip title={activity.activityTitle} pointerPosition="below" usePortal={true}>
                    <Box display="flex" flexDirection="row" width={size.width} height={size.height} tabIndex={-1}>
                        {activity.activityImage ? (
                            <Image width={size.imageWidth} height="100%" objectFit="cover" src={activity.activityImage} alt={cms[367068]} />
                        ) : (
                            <Box maxWidth={size.imageWidth} width="100%" height="100%" backgroundColor="base.background" display="flex" justifyContent="center" alignItems="center">
                                <Icon id="fill.activity.ferris-wheel" size="20" />
                            </Box>
                        )}
                        <Box display="flex" flexDirection="row" ms={size.marginS} my={size.marginY} me={size.marginE} tabIndex={-1}>
                            <Box data-testid="continue-your-booking-info" display="flex" flexDirection="column" flexGrow={1} rowGap="4" me={size.marginS} justifyContent="center">
                                <Typography variant="body-s-strong" lineClamp={2}>
                                    {activity.activityTitle}
                                </Typography>
                                <Typography display="inline-flex" variant="caption-default" textColor="base.subtle" lineClamp={1}>
                                    {getTravelerInfo(activity, cms)}
                                </Typography>
                            </Box>
                            <Box alignContent="center">
                                <Icon id="fill.arrow.thin-right" color="base.subtle" size="16" />
                            </Box>
                        </Box>
                    </Box>
                </Tooltip>
            </Card>
        </Link>
    );
};
ContinueYourBookingCard.displayName = 'ContinueYourBookingItem';

const ContinueYourBookingComponent: React.FC<ContinueYourBookingProps> = (props) => {
    const config = useSelector(selectConfigFromBootstrap);
    const pageTypeId = useSelector(selectPageTypeId);
    const isMockMode = config.isMockData;
    const size = props.screenSize.tabletOrDesktop ? tabletOrDesktop : mobile;

    const [activities, setActivities] = React.useState<ContinueYourBookingActivity[]>([]);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        const fetchActivities = async () => {
            try {
                const response = await getContinueYourBookingActivities();

                const rawContentMetaInfo =
                    (response.body as unknown as CuscoCampaignResponse)?.result?.sections?.[0]?.cards?.find((card) => card.cardId === ActivitiesCampaignCardId)?.content
                        ?.contentMetaInfo || '';
                const parsedActivities = parseContentMetaInfoToContinueYourBookingActivities(rawContentMetaInfo);
                setActivities(parsedActivities);
                measureCuscoCampaignsApiResponse(MeasurementStatus.Success, rawContentMetaInfo, '', config, pageTypeId);
            } catch (error) {
                measureCuscoCampaignsApiResponse(MeasurementStatus.Failed, '', `Failed to fetch activities from Cusco Campaign:, ${error}`, config, pageTypeId);
            } finally {
                setLoading(false);
            }
        };

        if (isMockMode) {
            setActivities(MockActivityContentMetaInfo);
            setLoading(false);
        } else {
            fetchActivities().catch((error) => console.error('Failed to fetch activities:', error));
        }
    }, [config, isMockMode, pageTypeId]);

    React.useEffect(() => {
        if (!loading && activities && activities.length > 0) {
            trackElementEvent<ActivitiesContinueYourBookingContainerSeenEventActivitiesFieldsV1>(ActivitiesContinueYourBookingContainerSeenEventActivitiesManifestV1.ids, {});
        }
    }, [activities, loading]);

    if (loading) {
        return (
            <Box display="flex" flexDirection="column" width="100%" data-element-name="activities-continue-your-booking-container" data-testid="continue-your-booking-container">
                <Typography variant="heading-l">{config.cms[367068]}</Typography>
                <ContinueYourBookingPlaceholder size={size} />
            </Box>
        );
    }

    if (!activities || activities.length === 0) {
        return null;
    }

    return (
        <Box display="flex" flexDirection="column" width="100%" data-element-name="activities-continue-your-booking-container" data-testid="continue-your-booking-container">
            <Typography variant="heading-l">{config.cms[367068]}</Typography>
            <HorizontalScroll gap="8" mt="12">
                {activities.map((activity, index) => (
                    <ContinueYourBookingCard key={index} cms={config.cms} language={config.language} size={size} onClick={props.onClickHandler} activity={activity} index={index} />
                ))}
            </HorizontalScroll>
        </Box>
    );
};

ContinueYourBookingComponent.displayName = 'ContinueYourBooking';

export default connect(mapStateToProps, mapDispatchToProps)(ContinueYourBookingComponent);
